import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Spinner } from "./spinner";
import { cn, FOCUS_RING_CLASSES } from "@repo/ui/lib/utils";

// Deleted unused dark styles.
const buttonVariants = cva(cn("py-2",
// Make explicit that all buttons have same py.
"inline-flex items-center justify-center gap-2", "whitespace-nowrap", "rounded-md",
// AoPS modified: `text-sm` is too small.
"font-medium", "transition-all", "disabled:cursor-not-allowed disabled:opacity-50",
// Allow pointer events to not prevent correct browser behavior. Also, set correct cursor.
"active:opacity-60",
// Add pressed styles.
"[&_svg]:size-4",
// Icon size always = text-base without the incredibly scuffed selector that Shad tries.
"shrink-0 [&_svg]:shrink-0",
// More accessible focus and invalid rings.
// AoPS Modified: Factor out focus ring classes and move outline-none to
// these classes
FOCUS_RING_CLASSES, "aria-invalid:ring-destructive aria-invalid:ring-[2px] aria-invalid:ring-offset-1"), {
  variants: {
    // Set on-brand & accessible button colors.
    // Removed text colors on hover because the text colors don't change on hover.
    // Disable hover styles when disabled.
    variant: {
      default: cn("bg-brand-bold text-brand-primary", "hover:bg-brand-bold-hover disabled:hover:bg-brand-bold", "shadow-xs"),
      secondary: cn("bg-neutral-tertiary text-secondary-foreground", "hover:bg-neutral-tertiary-hover disabled:hover:bg-neutral-tertiary", "shadow-xs"),
      destructive: cn("bg-destructive text-destructive-foreground", "hover:bg-brand-red-700 disabled:hover:bg-destructive", "shadow-xs"),
      outline: cn("bg-background", "hover:bg-neutral-tertiary-hover disabled:hover:bg-background", "border shadow-xs"),
      ghost: cn("hover:bg-neutral-tertiary-hover disabled:hover:bg-transparent"),
      // For the `link` variant, I removed the underline offset from the
      // default to match how the styles are defined in the Figma UI kit
      // and also to match the style of regular links, e.g. `<a>`/`<Link>`.
      link: cn("text-brand-bold", "hover:underline disabled:hover:no-underline")
    },
    size: {
      // Removed unnecessary nested icon padding for consistency.

      default: cn("h-9 px-4"),
      // Moved py to base className list because all buttons share same py.
      icon: cn("size-9"),
      sm: cn("h-8 px-3"),
      lg: cn("h-10 px-8") // Wider padding
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
function Button(t0) {
  const $ = _c(20);
  let className;
  let props;
  let size;
  let t1;
  let t2;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      asChild: t1,
      pending: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = size;
    $[4] = t1;
    $[5] = t2;
    $[6] = variant;
  } else {
    className = $[1];
    props = $[2];
    size = $[3];
    t1 = $[4];
    t2 = $[5];
    variant = $[6];
  }
  const asChild = t1 === undefined ? false : t1;
  const pending = t2 === undefined ? false : t2;
  const Comp = asChild ? Slot : "button";
  let t3;
  if ($[7] !== asChild || $[8] !== pending || $[9] !== props) {
    t3 = pending && !asChild ? {
      disabled: true,
      children: <><Spinner />{props.children}</>
    } : {};
    $[7] = asChild;
    $[8] = pending;
    $[9] = props;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const pendingProps = t3;
  let t4;
  if ($[11] !== className || $[12] !== size || $[13] !== variant) {
    t4 = cn(buttonVariants({
      variant,
      size,
      className
    }));
    $[11] = className;
    $[12] = size;
    $[13] = variant;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== Comp || $[16] !== pendingProps || $[17] !== props || $[18] !== t4) {
    t5 = <Comp data-slot="button" className={t4} {...props} {...pendingProps} />;
    $[15] = Comp;
    $[16] = pendingProps;
    $[17] = props;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}
export { Button, buttonVariants };